/**
 * ArtworkStudio — /artwork (super admin). Compose the Social Go / Social Pro roundabout with sliders
 * and export a high-res transparent PNG for print.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Download, ChevronUp, ChevronDown, RotateCcw, Loader2, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PRODUCTS, videosForSlot } from './ProductShowcase';
import { CarouselScene } from './artwork/carouselScene';
import { posterTexture, instagramTexture } from './artwork/cardTextures';
import './ArtworkStudio.css';

const API = process.env.REACT_APP_BACKEND_URL;
const IG_AVATAR = 'https://nichecomfiles.co.uk/proposal-examples/defaults/gilmours-avatar-2026.png';

const PRESETS = {
  'Front row': { rot: -42, tilt: 0, persp: 1600, rad: 2.2, step: 17, front: 6, face: -0.5, ring: 1, dim: 0.55, shadow: 1, w: 1800, h: 800 },
  'From above': { rot: -42, tilt: 10, persp: 1600, rad: 2.2, step: 17, front: 6, face: -0.5, ring: 1, dim: 0.55, shadow: 1, w: 1800, h: 840 },
  'Fanned arc': { rot: -31, tilt: 12.5, persp: 2400, rad: 2.9, step: 17.5, front: 6, face: -0.5, ring: 0.97, dim: 0.55, shadow: 1, w: 2000, h: 820 },
  'Seven cards': { rot: -48, tilt: 6, persp: 1800, rad: 2.4, step: 16, front: 7, face: -0.35, ring: 1, dim: 0.55, shadow: 1, w: 2000, h: 820 },
  'Flat line-up': { rot: -35, tilt: 0, persp: 3200, rad: 3.2, step: 14, front: 6, face: 0, ring: 1, dim: 0.7, shadow: 0.8, w: 2200, h: 760 },
  'Low angle': { rot: -42, tilt: -12, persp: 1400, rad: 2.2, step: 17, front: 6, face: -0.5, ring: 1, dim: 0.55, shadow: 1, w: 1800, h: 840 },
  'Hero three': { rot: -22, tilt: 4, persp: 1800, rad: 2.6, step: 22, front: 3, face: -0.4, ring: 1.15, dim: 0.5, shadow: 1, w: 1600, h: 900 },
  'Roundabout': { rot: -36, tilt: 0, persp: 1200, rad: 1, step: 36, front: 10, face: -0.5, ring: 1, dim: 0.55, shadow: 1, w: 1700, h: 800 },
};

const SLIDERS = [
  { key: 'rot', label: 'Rotation', min: -180, max: 180, step: 1, unit: '°' },
  { key: 'tilt', label: 'Camera tilt', min: -30, max: 30, step: 0.5, unit: '°' },
  { key: 'persp', label: 'Perspective', min: 600, max: 3200, step: 50, unit: 'px' },
  { key: 'rad', label: 'Spread (radius)', min: 0.8, max: 3.2, step: 0.05, unit: '×' },
  { key: 'step', label: 'Card spacing', min: 6, max: 60, step: 0.5, unit: '°' },
  { key: 'front', label: 'Cards in front', min: 1, max: 10, step: 1, unit: '' },
  { key: 'face', label: 'Card angle', min: -1, max: 1, step: 0.05, unit: '', hint: '− inward · 0 flat · + outward' },
  { key: 'ring', label: 'Ring size', min: 0.5, max: 1.3, step: 0.01, unit: '×' },
  { key: 'dim', label: 'Back-card brightness', min: 0.1, max: 1, step: 0.05, unit: '' },
  { key: 'shadow', label: 'Shadow', min: 0, max: 1.5, step: 0.05, unit: '' },
  { key: 'w', label: 'Canvas width', min: 900, max: 2800, step: 20, unit: 'px' },
  { key: 'h', label: 'Canvas height', min: 500, max: 1500, step: 20, unit: 'px' },
];

const Slider = ({ s, value, onChange }) => (
  <label className="aw-slider" data-testid={`aw-slider-${s.key}`}>
    <span className="aw-slider-head">
      <span>{s.label}</span>
      <span className="aw-slider-val">{Number.isInteger(s.step) ? value : value.toFixed(2).replace(/\.?0+$/, '')}{s.unit}</span>
    </span>
    <input type="range" min={s.min} max={s.max} step={s.step} value={value} onChange={(e) => onChange(parseFloat(e.target.value))} />
    {s.hint && <span className="aw-slider-hint">{s.hint}</span>}
  </label>
);

export default function ArtworkStudio({ user }) {
  const canvasRef = useRef(null);
  const sceneRef = useRef(null);
  const [videos, setVideos] = useState(null);
  const [variant, setVariant] = useState('example');
  const [tab, setTab] = useState('social-pro');
  const [params, setParams] = useState(PRESETS['Front row']);
  const [order, setOrder] = useState(() => PRODUCTS.filter((p) => p.tiers.includes('social-pro')).map((p) => ({ key: p.key, label: p.label, on: true })));
  const [texReady, setTexReady] = useState(0);
  const [loadingTex, setLoadingTex] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [scale, setScale] = useState(4);
  const [bg, setBg] = useState('transparent');
  const [progress, setProgress] = useState(0);
  const [saved, setSaved] = useState([]);
  const [presetName, setPresetName] = useState('');
  const [saving, setSaving] = useState(false);
  const texCache = useRef(new Map());

  useEffect(() => {
    axios.get(`${API}/api/example-videos/public`).then((r) => setVideos(r.data)).catch(() => setVideos({ introductions: [], examples: [] }));
    axios.get(`${API}/api/artwork/presets`).then((r) => setSaved(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    sceneRef.current = new CarouselScene(canvasRef.current);
    return () => { sceneRef.current?.dispose(); sceneRef.current = null; };
  }, []);

  useEffect(() => {
    setOrder(PRODUCTS.filter((p) => p.tiers.includes(tab)).map((p) => ({ key: p.key, label: p.label, on: true })));
  }, [tab]);

  // Card → poster url for the chosen variant
  const posters = useMemo(() => {
    if (!videos) return {};
    const slotMap = {};
    [...(videos.introductions || []), ...(videos.examples || [])].forEach((v) => { slotMap[v.id] = v; });
    const out = {};
    PRODUCTS.forEach((p) => {
      if (p.type === 'images') { out[p.key] = { images: p.images }; return; }
      const intro = videosForSlot(slotMap, p.intro);
      const example = videosForSlot(slotMap, p.example);
      const set = variant === 'intro' ? [...intro, ...example] : [...example, ...intro];
      const poster = set.find((v) => v.poster_url)?.poster_url;
      if (poster) out[p.key] = { poster };
    });
    return out;
  }, [videos, variant]);

  // Build textures (cached per poster url)
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene || !videos) return undefined;
    let alive = true;
    setLoadingTex(true);
    const wanted = order.filter((o) => o.on && posters[o.key]);
    Promise.all(wanted.map(async (o) => {
      const src = posters[o.key];
      const cacheKey = src.poster || `ig:${o.key}`;
      try {
        if (!texCache.current.has(cacheKey)) {
          const tex = src.poster ? await posterTexture(src.poster, scene.renderer) : await instagramTexture(src.images, IG_AVATAR, scene.renderer);
          texCache.current.set(cacheKey, tex);
        }
        return [o.key, texCache.current.get(cacheKey)];
      } catch (e) {
        toast.error(`${o.label}: poster failed to load`);
        return null;
      }
    })).then((pairs) => {
      if (!alive) return;
      scene.setCards(new Map(pairs.filter(Boolean)));
      setTexReady((n) => n + 1);
      setLoadingTex(false);
    });
    return () => { alive = false; };
  }, [videos, posters, order]);

  const visibleKeys = useMemo(() => order.filter((o) => o.on && posters[o.key] && sceneRef.current?.cards.has(o.key)).map((o) => o.key), [order, posters, texReady]);

  // Preview render — on every change
  const draw = useCallback(() => {
    const scene = sceneRef.current;
    const canvas = canvasRef.current;
    if (!scene || !canvas) return;
    scene.layout(visibleKeys, params);
    const cssW = canvas.parentElement.clientWidth;
    const cssH = Math.round((cssW * params.h) / params.w);
    canvas.style.width = `${cssW}px`;
    canvas.style.height = `${cssH}px`;
    scene.render(params.w, params.h, cssW / params.w * Math.min(window.devicePixelRatio || 1, 2), bg === 'transparent' ? null : bg);
  }, [visibleKeys, params, bg]);

  useEffect(() => {
    const id = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(id);
  }, [draw, texReady]);

  useEffect(() => {
    window.addEventListener('resize', draw);
    return () => window.removeEventListener('resize', draw);
  }, [draw]);

  const set = (k) => (v) => setParams((p) => ({ ...p, [k]: v }));
  const move = (i, dir) => setOrder((o) => {
    const j = i + dir;
    if (j < 0 || j >= o.length) return o;
    const next = [...o];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });

  const exportPNG = async () => {
    const scene = sceneRef.current;
    if (!scene) return;
    setExporting(true);
    setProgress(0);
    try {
      await new Promise((r) => setTimeout(r, 30));
      const blob = await scene.exportPNG(params.w, params.h, scale, bg === 'transparent' ? null : bg, setProgress);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `social-pro-carousel-${params.w * scale}x${params.h * scale}-${Date.now()}.png`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
      toast.success(`Rendered ${params.w * scale} × ${params.h * scale}px`);
    } catch (e) {
      toast.error(`Render failed: ${e.message}`);
    } finally {
      setExporting(false);
      draw();
    }
  };

  const applySaved = (p) => {
    setTab(p.tab || 'social-pro');
    setVariant(p.variant || 'example');
    if (p.scale) setScale(p.scale);
    if (p.bg) setBg(p.bg);
    setParams({ ...PRESETS['Front row'], ...p.params });
    if (p.order?.length) setTimeout(() => setOrder(p.order), 0); // after the tab effect resets the list
  };

  const savePreset = async () => {
    const name = presetName.trim();
    if (!name) { toast.error('Give the preset a name'); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/api/artwork/presets`, { name, params, order, variant, tab, scale, bg });
      setSaved((list) => [...list.filter((p) => p.id !== r.data.id), r.data]);
      setPresetName('');
      toast.success(`Saved "${name}"`);
    } catch (e) {
      toast.error('Could not save preset');
    } finally {
      setSaving(false);
    }
  };

  const deletePreset = async (p) => {
    try {
      await axios.delete(`${API}/api/artwork/presets/${p.id}`);
      setSaved((list) => list.filter((x) => x.id !== p.id));
      toast.success(`Deleted "${p.name}"`);
    } catch (e) {
      toast.error('Could not delete preset');
    }
  };

  if (user?.role !== 'super_admin') {
    return <div className="aw-denied" data-testid="aw-denied">The artwork studio is for super admins only.</div>;
  }

  return (
    <div className="aw" data-testid="artwork-studio">
      <aside className="aw-panel" data-testid="aw-panel">
        <div className="aw-panel-head">
          <span className="aw-eyebrow">Artwork studio</span>
          <h1 className="aw-title">Social Pro roundabout</h1>
        </div>

        <div className="aw-group">
          <span className="aw-group-title">Presets</span>
          <div className="aw-chips">
            {Object.keys(PRESETS).map((name) => (
              <button key={name} type="button" className="aw-chip" onClick={() => setParams(PRESETS[name])} data-testid={`aw-preset-${name.toLowerCase().replace(/\s+/g, '-')}`}>{name}</button>
            ))}
          </div>
          {saved.length > 0 && (
            <ul className="aw-saved" data-testid="aw-saved-presets">
              {saved.map((p) => (
                <li key={p.id} data-testid={`aw-saved-${p.id}`}>
                  <button type="button" className="aw-saved-apply" onClick={() => applySaved(p)} data-testid={`aw-saved-apply-${p.id}`}>{p.name}</button>
                  <button type="button" className="aw-saved-del" onClick={() => deletePreset(p)} aria-label={`Delete ${p.name}`} data-testid={`aw-saved-delete-${p.id}`}><Trash2 size={13} /></button>
                </li>
              ))}
            </ul>
          )}
          <form className="aw-save" onSubmit={(e) => { e.preventDefault(); savePreset(); }} data-testid="aw-save-form">
            <input type="text" value={presetName} onChange={(e) => setPresetName(e.target.value)} placeholder="Save current setup as…" maxLength={60} data-testid="aw-save-name" />
            <button type="submit" disabled={saving || !presetName.trim()} data-testid="aw-save-btn">{saving ? <Loader2 size={14} className="aw-spin" /> : <Save size={14} />} Save</button>
          </form>
        </div>

        <div className="aw-group">
          <span className="aw-group-title">Posters</span>
          <div className="aw-seg" data-testid="aw-variant">
            <button type="button" className={variant === 'example' ? 'is-active' : ''} onClick={() => setVariant('example')} data-testid="aw-variant-example">Branded examples</button>
            <button type="button" className={variant === 'intro' ? 'is-active' : ''} onClick={() => setVariant('intro')} data-testid="aw-variant-intro">Intros</button>
          </div>
          <div className="aw-seg" data-testid="aw-tab">
            <button type="button" className={tab === 'social-pro' ? 'is-active' : ''} onClick={() => setTab('social-pro')} data-testid="aw-tab-social-pro">Social Pro</button>
            <button type="button" className={tab === 'social-go' ? 'is-active' : ''} onClick={() => setTab('social-go')} data-testid="aw-tab-social-go">Social Go</button>
          </div>
        </div>

        <div className="aw-group">
          <span className="aw-group-title">Layout</span>
          {SLIDERS.slice(0, 10).map((s) => <Slider key={s.key} s={{ ...s, max: s.key === 'front' ? Math.max(1, visibleKeys.length) : s.max }} value={params[s.key]} onChange={set(s.key)} />)}
        </div>

        <div className="aw-group">
          <span className="aw-group-title">Card order <em>(first = left of the front arc)</em></span>
          <ul className="aw-order" data-testid="aw-order">
            {order.map((o, i) => (
              <li key={o.key} className={o.on && posters[o.key] ? '' : 'is-off'} data-testid={`aw-order-${o.key}`}>
                <input type="checkbox" checked={o.on} onChange={(e) => setOrder((prev) => prev.map((x) => (x.key === o.key ? { ...x, on: e.target.checked } : x)))} aria-label={`Include ${o.label}`} />
                <span className="aw-order-label">{o.label}{!posters[o.key] && <small> · no poster</small>}</span>
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up"><ChevronUp size={14} /></button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === order.length - 1} aria-label="Move down"><ChevronDown size={14} /></button>
              </li>
            ))}
          </ul>
        </div>

        <div className="aw-group">
          <span className="aw-group-title">Canvas &amp; export</span>
          {SLIDERS.slice(10).map((s) => <Slider key={s.key} s={s} value={params[s.key]} onChange={set(s.key)} />)}
          <div className="aw-row">
            <span>Scale</span>
            <div className="aw-seg aw-seg-sm" data-testid="aw-scale">
              {[2, 3, 4, 5].map((n) => <button key={n} type="button" className={scale === n ? 'is-active' : ''} onClick={() => setScale(n)} data-testid={`aw-scale-${n}`}>{n}×</button>)}
            </div>
          </div>
          <div className="aw-row">
            <span>Background</span>
            <div className="aw-seg aw-seg-sm" data-testid="aw-bg">
              <button type="button" className={bg === 'transparent' ? 'is-active' : ''} onClick={() => setBg('transparent')} data-testid="aw-bg-transparent">Transparent</button>
              <button type="button" className={bg === '#1a472a' ? 'is-active' : ''} onClick={() => setBg('#1a472a')} data-testid="aw-bg-green">Brand green</button>
              <button type="button" className={bg === '#ffffff' ? 'is-active' : ''} onClick={() => setBg('#ffffff')} data-testid="aw-bg-white">White</button>
            </div>
          </div>
          <button type="button" className="aw-export" onClick={exportPNG} disabled={exporting || loadingTex || visibleKeys.length === 0} data-testid="aw-export-btn">
            {exporting ? <Loader2 size={18} className="aw-spin" /> : <Download size={18} />}
            {exporting ? `Rendering… ${Math.round(progress * 100)}%` : `Render PNG · ${params.w * scale} × ${params.h * scale}px`}
          </button>
          <button type="button" className="aw-reset" onClick={() => setParams(PRESETS['Front row'])} data-testid="aw-reset-btn"><RotateCcw size={13} /> Reset</button>
        </div>
      </aside>

      <main className="aw-stage" data-testid="aw-stage">
        <div className={`aw-canvas-wrap ${bg === 'transparent' ? 'is-checker' : ''}`} style={bg !== 'transparent' ? { background: bg } : undefined}>
          <canvas ref={canvasRef} data-testid="aw-canvas" />
          {(loadingTex || !videos) && <div className="aw-loading" data-testid="aw-loading"><Loader2 size={20} className="aw-spin" /> Loading posters…</div>}
        </div>
        <p className="aw-note">Preview is what you'll get — drag the sliders, reorder cards, then Render PNG. Exports are transparent unless you pick a background.</p>
      </main>
    </div>
  );
}
